import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { saveFarmLog, getFarmLogs, getFarmLog } from '../lib/supabase'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const emptyChemRow = () => ({ product: '', amount: '', reason: '' })

export default function FarmLog() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')

  const [tab, setTab] = useState('new')
  const [logs, setLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const [activeCycles, setActiveCycles] = useState([])

  const [form, setForm] = useState({
    log_date: ugandaDate(),
    cycle_id: '',
    ph: '', ec: '', water_temp: '', room_temp: '', humidity: '',
    reservoir_level: 'Full',
    plant_health: 'Good — minor issues noted',
    pests_spotted: 'None',
    observations: '', equipment_issues: '',
    chemical_rows: [emptyChemRow()],
  })

  useEffect(() => {
    loadActiveCycles()
    if (tab === 'history') loadLogs()
    if (editId) loadForEdit(editId)
  }, [tab, editId])

  const loadActiveCycles = async () => {
    const { data } = await supabase
      .from('crop_cycles')
      .select('id, cycle_name, crop, system, current_stage')
      .not('current_stage', 'eq', 'Complete')
      .order('transplant_date', { ascending: false })
    setActiveCycles(data || [])
  }

  const loadLogs = async () => {
    setLogsLoading(true)
    const { data } = await getFarmLogs({ limit: 30 })
    setLogs(data || [])
    setLogsLoading(false)
  }

  const loadForEdit = async (id) => {
    const { data } = await getFarmLog(id)
    if (data) setForm({ ...data, chemical_rows: data.chemical_rows || [emptyChemRow()] })
  }

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }))

  const setChemRow = (i, field, val) => {
    const rows = [...form.chemical_rows]
    rows[i] = { ...rows[i], [field]: val }
    setForm(f => ({ ...f, chemical_rows: rows }))
  }

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false)
    const payload = { ...form, user_id: user.id, ...(editId ? { id: editId } : {}) }
    const { error: err } = await saveFarmLog(payload)
    if (err) { setError(err.message); setSaving(false); return }

    // Auto-reduce stock and create WIP entries for chemicals used
    const validChems = form.chemical_rows.filter(r => r.product && r.amount)
    if (validChems.length > 0) {
      // Find matching stock items
      const { data: stockItems } = await supabase
        .from('stock_items').select('id, name, unit')
      
      for (const chem of validChems) {
        // Try to match product name to stock item
        const match = (stockItems || []).find(s =>
          s.name.toLowerCase().includes(chem.product.toLowerCase()) ||
          chem.product.toLowerCase().includes(s.name.toLowerCase())
        )
        
        // Parse quantity from amount string (e.g. "50ml" → 50)
        const qtyMatch = chem.amount?.toString().match(/[\d.]+/)
        const qty = qtyMatch ? parseFloat(qtyMatch[0]) : 0

        if (match && qty > 0) {
          // 1. Reduce stock (Raw Materials out)
          await supabase.from('stock_movements').insert({
            stock_item_id: match.id,
            movement_type: 'out',
            quantity: qty,
            unit: match.unit,
            movement_date: form.log_date,
            reason: `Used in farm — ${chem.reason || 'Applied to crops'}${form.cycle_id ? ' (Cycle linked)' : ''}`,
            user_id: user.id,
          })

          // 2. Create WIP journal entry (Raw Materials → WIP)
          if (form.cycle_id) {
            await supabase.from('wip_journal').insert({
              entry_date: form.log_date,
              type: 'raw_to_wip',
              stock_item_id: match.id,
              item_name: match.name,
              quantity: qty,
              unit: match.unit,
              unit_cost: 0, // cost tracked at purchase, not here
              crop_name: activeCycles.find(c => c.id === form.cycle_id)?.crop || '',
              notes: `Farm Log — ${chem.reason || 'Applied to crops'}`,
              user_id: user.id,
            })
          }
        }
      }
    }

    setSaved(true); setSaving(false)
    setTimeout(() => setSaved(false), 3000)
    if (!editId) setForm({ ...form, log_date: ugandaDate(), cycle_id: '', ph: '', ec: '', water_temp: '', room_temp: '', humidity: '', observations: '', equipment_issues: '', chemical_rows: [emptyChemRow()] })
  }

  return (
    <AppLayout title="Daily Farm Log">
      <div className="section-header">
        <div className="section-icon">🌱</div>
        <div className="section-info">
          <h2>Daily Farm Log</h2>
          <p>Complete every day before 4:30 PM. All entries are saved to the cloud.</p>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>📝 New Entry</button>
        <button className={`tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>📚 History</button>
      </div>

      {tab === 'new' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Farm log saved successfully to the cloud!</div>}
          {error && <div className="alert alert-red">❌ {error}</div>}

          <div className="grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input className="form-input" type="date" value={form.log_date} onChange={e => set('log_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Logged by</label>
              <input className="form-input" value={profile?.full_name || ''} readOnly style={{ background: '#f0f0e8' }} />
            </div>
          </div>

          {/* Cycle selector */}
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">🌿 Which crop cycle are you working on today?</label>
            <select className="form-select" value={form.cycle_id} onChange={e => set('cycle_id', e.target.value)}>
              <option value="">— Select active cycle (optional) —</option>
              {activeCycles.map(c => (
                <option key={c.id} value={c.id}>{c.cycle_name} — {c.crop}{c.variety ? ` (${c.variety})` : ''} · {c.system}{c.zone && c.zone !== 'Full frame' ? ` · ${c.zone}` : ''}</option>
              ))}
            </select>
            {activeCycles.length === 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                No active cycles. Start one in <strong>Farm Operations → Crop Cycles</strong> on transplant day.
              </div>
            )}
            {form.cycle_id && (
              <div style={{ fontSize: '0.72rem', color: 'var(--green-mid)', marginTop: 4, fontWeight: 700 }}>
                ✅ Inputs logged today will be linked to this cycle for full traceability
              </div>
            )}
          </div>

          <hr className="divider" />
          <div style={{ fontWeight: 700, marginBottom: 12 }}>💧 Water & Nutrient Readings</div>
          <div className="grid-3">
            {[['ph', 'pH', '5.8–6.2', 'e.g. 6.1'], ['ec', 'EC (mS/cm)', '0.8–2.0', 'e.g. 1.4'], ['water_temp', 'Water Temp (°C)', '18–22°C', 'e.g. 20'], ['room_temp', 'Room Temp (°C)', '18–24°C', 'e.g. 22'], ['humidity', 'Humidity (%)', '50–70%', 'e.g. 65']].map(([field, label, target, ph]) => (
              <div className="form-group" key={field}>
                <label className="form-label">{label} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>({target})</span></label>
                <input className="form-input" placeholder={ph} value={form[field]} onChange={e => set(field, e.target.value)} />
              </div>
            ))}
            <div className="form-group">
              <label className="form-label">Reservoir Level</label>
              <select className="form-select" value={form.reservoir_level} onChange={e => set('reservoir_level', e.target.value)}>
                {['Full', '75%', '50% — Topped up', 'Below 20% — Urgent'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <hr className="divider" />
          <div style={{ fontWeight: 700, marginBottom: 12 }}>🌱 Plant Observations</div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Overall Plant Health</label>
              <select className="form-select" value={form.plant_health} onChange={e => set('plant_health', e.target.value)}>
                {['Excellent — all looking great', 'Good — minor issues noted', 'Concerning — issues require action', 'Poor — urgent intervention needed'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Pests or Disease Spotted?</label>
              <select className="form-select" value={form.pests_spotted} onChange={e => set('pests_spotted', e.target.value)}>
                {['None', 'Yes — see observations below'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Observations (issues, what you saw, what you did)</label>
            <textarea className="form-textarea" placeholder="e.g. Row 3 lettuce showing tip burn. Increased fan speed. Will monitor tomorrow." value={form.observations} onChange={e => set('observations', e.target.value)} />
          </div>

          <div className="alert alert-amber" style={{ marginBottom:14 }}>
            🌾 Record harvests separately in the <strong>Harvest Log</strong> page — this keeps harvest records clean and auto-updates Stores inventory.
          </div>

          <hr className="divider" />
          <div style={{ fontWeight: 700, marginBottom: 10 }}>🧪 Chemicals / Nutrients Added Today</div>
          <div className="table-wrap" style={{ marginBottom: 8 }}>
            <table>
              <thead><tr><th>Product</th><th>Amount</th><th>Reason</th><th style={{ width: 32 }}></th></tr></thead>
              <tbody>
                {form.chemical_rows.map((row, i) => (
                  <tr key={i}>
                    {['product', 'amount', 'reason'].map(f => (
                      <td key={f}><input className="form-input" placeholder={f === 'product' ? 'e.g. pH Down' : f === 'amount' ? 'e.g. 50ml' : 'Why?'} value={row[f]} onChange={e => setChemRow(i, f, e.target.value)} /></td>
                    ))}
                    <td>
                      {form.chemical_rows.length > 1 && (
                        <button className="btn-del-row" onClick={() => setForm(f => ({ ...f, chemical_rows: f.chemical_rows.filter((_, j) => j !== i) }))}>✕</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setForm(f => ({ ...f, chemical_rows: [...f.chemical_rows, emptyChemRow()] }))}>+ Add Chemical Entry</button>

          <hr className="divider" />
          <div className="form-group">
            <label className="form-label">Equipment Issues</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }} placeholder="e.g. Pump 2 making grinding noise. Called supplier." value={form.equipment_issues} onChange={e => set('equipment_issues', e.target.value)} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>
              {saving ? <><span className="spinner" /> Saving...</> : '💾 Save to Cloud'}
            </button>
            <button className="btn btn-ghost" onClick={() => window.print()}>🖨️ Print</button>
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="card">
          <div className="card-title">Farm Log History</div>
          <div className="card-subtitle">Last 30 entries — newest first</div>
          {logsLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
          ) : logs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No logs yet</h3>
              <p>Start by creating your first farm log entry.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th><th>Logged By</th><th>pH</th><th>EC</th><th>Plant Health</th><th>Pests</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id}>
                      <td style={{ fontWeight: 600 }}>{new Date(log.log_date).toLocaleDateString('en-UG')}</td>
                      <td>{log.profiles?.full_name || '—'}</td>
                      <td><span className="badge badge-green">{log.ph || '—'}</span></td>
                      <td>{log.ec || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{log.plant_health?.split(' — ')[0]}</td>
                      <td><span className={`badge ${log.pests_spotted === 'None' ? 'badge-green' : 'badge-red'}`}>{log.pests_spotted === 'None' ? 'None' : '⚠️ Yes'}</span></td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/farm-log?edit=${log.id}`)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
